import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import authService from '../../../infrastructure/services/authService';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const LoginOTPVerificationModal = ({ open, onClose, verificationData, onSuccess }) => {
  const { userId, phoneNumber, email, password } = verificationData || {};

  // OTP state
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const inputsRef = useRef([]);
  const [verifying, setVerifying] = useState(false);
  const [localError, setLocalError] = useState('');
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const [successState, setSuccessState] = useState(false);
  const [resending, setResending] = useState(false);

  // Mask functions
  const maskMobile = (val) => {
    if (!val) return '••••••••••';
    const digitsOnly = val.replace(/\D/g, '');
    if (digitsOnly.length < 4) return val;
    const last2 = digitsOnly.slice(-2);
    return `${val.slice(0, val.length - 2).replace(/\d/g, '•')}${last2}`;
  };

  const maskEmail = (val) => {
    if (!val) return '••••@••••.•••';
    const parts = val.split('@');
    if (parts.length < 2) return val;
    const domain = parts[1];
    const local = parts[0].slice(0, 2) + '••••';
    return `${local}@${domain}`;
  };

  const maskedMobile = maskMobile(phoneNumber);
  const maskedEmail = maskEmail(email);

  // Focus first input box
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
      setDigits(Array(OTP_LENGTH).fill(''));
      setLocalError('');
      setTimer(RESEND_SECONDS);
      setSuccessState(false);
    }
  }, [open]);

  // Timer countdown
  useEffect(() => {
    if (timer <= 0 || !open) return;
    const id = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(id);
  }, [timer, open]);

  const focusInput = (i) => inputsRef.current[i]?.focus();

  const handleChange = (i, raw) => {
    const v = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setLocalError('');
    if (v && i < OTP_LENGTH - 1) focusInput(i + 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      if (digits[i]) {
        const next = [...digits];
        next[i] = '';
        setDigits(next);
      } else if (i > 0) {
        focusInput(i - 1);
        const next = [...digits];
        next[i - 1] = '';
        setDigits(next);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      focusInput(i - 1);
    } else if (e.key === 'ArrowRight' && i < OTP_LENGTH - 1) {
      focusInput(i + 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!text) return;
    const next = [...digits];
    let idx = 0;
    for (let i = 0; i < OTP_LENGTH; i++) {
      next[i] = text[idx] || '';
      idx++;
      if (idx >= text.length) break;
    }
    setDigits(next);
    const lastFilled = Math.min(text.length, OTP_LENGTH) - 1;
    if (lastFilled >= 0) focusInput(lastFilled);
  };

  const otpValue = digits.join('');
  const isValid = otpValue.length === OTP_LENGTH;

  const handleVerify = async (e) => {
    e?.preventDefault();
    if (!isValid) {
      setLocalError(`Please enter the ${OTP_LENGTH}-digit code.`);
      return;
    }
    setLocalError('');
    setVerifying(true);

    try {
      const payload = {
        otp_code: otpValue,
        phone_number: String(phoneNumber),
        userId: userId
      };

      if (email && email.trim() !== '') {
        payload.email = email;
      }

      if (password) {
        payload.password = password;
      }

      const result = await authService.completePhoneVerification(payload);

      setVerifying(false);
      setSuccessState(true);

      // Delay success callback slightly for animation effect
      setTimeout(() => {
        onSuccess(result);
      }, 1000);

    } catch (err) {
      setVerifying(false);
      setLocalError(err?.message || 'OTP verification failed');
    }
  };

  const handleResend = async () => {
    if (timer > 0 || verifying || resending) return;
    setResending(true);
    try {
      let formattedPhone = String(phoneNumber || '').trim();
      if (!formattedPhone.startsWith('+')) {
        const cleanDigits = formattedPhone.replace(/\D/g, '');
        formattedPhone = '+' + cleanDigits;
      } else {
        const cleanDigits = formattedPhone.slice(1).replace(/\D/g, '');
        formattedPhone = '+' + cleanDigits;
      }
      const payload = {
        userId,
        phone_number: formattedPhone,
        user_name: (email && email.trim() !== '') ? email.split('@')[0] : 'User'
      };
      if (email && email.trim() !== '') {
        payload.email = email;
      }
      await authService.requestPhoneVerification(payload);
      setDigits(Array(OTP_LENGTH).fill(''));
      focusInput(0);
      setTimer(RESEND_SECONDS);
      setLocalError('');
    } catch (err) {
      setLocalError(err?.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        if (!verifying && onClose) onClose();
      }}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={500}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '32px !important',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.6) !important',
          boxShadow: '0 24px 64px -12px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.5) !important',
          overflow: 'hidden',
          position: 'relative',
          padding: '12px',
          color: '#0f172a',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(255, 80, 45, 0.05) 0%, transparent 60%)',
            zIndex: 0,
            pointerEvents: 'none'
          }
        }
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', zIndex: 1 }}>
        <IconButton
          onClick={onClose}
          disabled={verifying}
          size="small"
          sx={{
            color: 'rgba(15, 23, 42, 0.65)',
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ position: 'relative', zIndex: 1, pt: 1, pb: 4, px: 3, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              color: '#0f172a',
              mb: 1.5,
              fontFamily: 'Poppins',
              fontSize: '1.65rem',
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            OTP Verification
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#334155',
              fontFamily: 'Poppins',
              lineHeight: 1.6,
              fontSize: '0.92rem',
              fontWeight: 500,
              maxWidth: '320px',
              margin: '0 auto'
            }}
          >
            Please enter the {OTP_LENGTH} digit OTP code sent to phone{' '}
            <strong style={{ color: '#ff502d', fontWeight: 700 }}>{maskedMobile}</strong>
            {email && email.trim() !== '' ? (
              <>
                {' and email '}
                <strong style={{ color: '#ff502d', fontWeight: 700 }}>{maskedEmail}</strong>
              </>
            ) : null}
            .
          </Typography>
        </Box>

        <form onSubmit={handleVerify}>
          <Box
            onPaste={handlePaste}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '10px',
              mb: 3.5
            }}
          >
            {Array.from({ length: OTP_LENGTH }).map((_, i) => (
              <input
                key={i}
                id={`otp-login-${i}`}
                ref={(el) => (inputsRef.current[i] = el)}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digits[i]}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                style={{
                  width: '46px',
                  height: '46px',
                  textAlign: 'center',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  borderRadius: '12px',
                  border: digits[i] ? '2px solid #ff502d' : '1px solid rgba(15, 23, 42, 0.18)',
                  background: digits[i] ? 'rgba(255, 80, 45, 0.08)' : 'rgba(15, 23, 42, 0.06)',
                  boxShadow: digits[i] ? '0 0 12px rgba(255, 80, 45, 0.15)' : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                  color: '#0f172a',
                  outline: 'none',
                  transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: 'Poppins'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#ff502d';
                  e.target.style.boxShadow = '0 0 15px rgba(255, 80, 45, 0.3)';
                  e.target.style.background = '#ffffff';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = digits[i] ? '#ff502d' : 'rgba(15, 23, 42, 0.18)';
                  e.target.style.boxShadow = digits[i] ? '0 0 12px rgba(255, 80, 45, 0.15)' : 'none';
                  e.target.style.background = digits[i] ? 'rgba(255, 80, 45, 0.08)' : 'rgba(15, 23, 42, 0.06)';
                }}
              />
            ))}
          </Box>

          {localError && (
            <Typography
              variant="caption"
              role="alert"
              sx={{
                color: '#e74c3c',
                display: 'block',
                mb: 2.5,
                fontWeight: 600,
                fontFamily: 'Poppins',
                backgroundColor: 'rgba(231, 76, 60, 0.08)',
                py: 1,
                px: 2,
                borderRadius: '8px',
                border: '1px solid rgba(231, 76, 60, 0.2)'
              }}
            >
              {localError}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
            <Typography variant="body2" sx={{ color: '#475569', fontFamily: 'Poppins', fontSize: '0.85rem', fontWeight: 500 }}>
              Didn't receive the OTP?
            </Typography>
            <button
              type="button"
              onClick={handleResend}
              disabled={timer > 0 || verifying || resending}
              style={{
                background: 'none',
                border: 'none',
                color: timer > 0 || verifying || resending ? '#94a3b8' : '#ff502d',
                fontWeight: '700',
                cursor: timer > 0 || verifying || resending ? 'not-allowed' : 'pointer',
                fontFamily: 'Poppins',
                padding: 0,
                fontSize: '0.85rem',
                textDecoration: 'underline'
              }}
            >
              {timer > 0 ? `Resend OTP in ${timer}s` : 'Resend OTP'}
            </button>
          </Box>

          <button
            type="submit"
            disabled={verifying || !isValid || successState}
            style={{
              height: '48px',
              border: 'none',
              borderRadius: '14px',
              background: successState
                ? 'linear-gradient(135deg, #2ecc71 0%, #27ae60 100%)'
                : 'linear-gradient(135deg, #ff502d 0%, #ff6b35 100%)',
              color: '#fff',
              fontWeight: '700',
              fontSize: '0.95rem',
              cursor: verifying || !isValid || successState ? 'not-allowed' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              fontFamily: 'Poppins',
              opacity: (!isValid && !successState) ? 0.6 : 1
            }}
            onMouseOver={(e) => {
              if (!verifying && isValid && !successState) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 12px 28px rgba(255, 80, 45, 0.45)';
              }
            }}
            onMouseOut={(e) => {
              if (!successState) {
                e.target.style.transform = 'none';
                e.target.style.boxShadow = '0 8px 24px rgba(255, 80, 45, 0.3)';
              }
            }}
          >
            {successState ? (
              'Verified Successfully!'
            ) : verifying ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CircularProgress size={18} sx={{ color: '#fff' }} />
                <span>Verifying...</span>
              </Box>
            ) : (
              'Verify & Login'
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LoginOTPVerificationModal;
